import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "../../api";

export function Field({ label, children }) {
  return <div><label className="text-xs text-gray-500 font-medium">{label}</label><div className="mt-0.5">{children}</div></div>;
}

export function Input({ label, value, onChange, type = "text", placeholder = "", list = undefined, testId = undefined }) {
  return <Field label={label}><input type={type} className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" value={value} onChange={onChange} placeholder={placeholder} list={list} data-testid={testId} /></Field>;
}

// Options may be plain strings or {value, label} objects.
//
// This used to read `o.value || o` and `o.label || o`. Both fall over on an empty
// string, which is a legitimate value here — it is how "All Units" is expressed:
//   - `o.value || o` handed the whole OBJECT to value=, which React stringifies to
//     "[object Object]". Choosing "All Units" therefore stored that literal text as
//     the user's unitId, which is how a user ends up with a unit that matches nothing
//     and can no longer be edited.
//   - `o.label || o` rendered the object as a child when a label was blank, which
//     React refuses outright: "Objects are not valid as a React child". A unit with
//     no name is enough to take the whole page down.
// Both are now read by key, with the fallbacks applied only where they are safe.
export function Select({ label, value, onChange, options }) {
  return (
    <Field label={label}>
      <select
        className="w-full border border-gray-300 rounded-lg px-3 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-300"
        value={value ?? ""}
        onChange={onChange}
      >
        {(options || []).map((o, i) => {
          const isObj = o !== null && typeof o === "object";
          const val = isObj ? (o.value ?? "") : o;
          const text = isObj ? o.label : o;
          return (
            <option key={`${String(val)}-${i}`} value={val}>
              {text === undefined || text === null || text === "" ? String(val) || "—" : text}
            </option>
          );
        })}
      </select>
    </Field>
  );
}

export function Btn({ onClick, children, variant = "primary", size = "sm", disabled = false, testId = "" }) {
  const base = "rounded-lg font-medium transition-colors disabled:opacity-50";
  const sz = size === "sm" ? "px-3 py-1.5 text-sm" : "px-4 py-2 text-sm";
  const v = variant === "primary" ? "bg-blue-600 text-white hover:bg-blue-700" : variant === "danger" ? "bg-red-500 text-white hover:bg-red-600" : "border border-gray-300 text-gray-600 hover:bg-gray-50";
  return <button onClick={onClick} className={`${base} ${sz} ${v}`} disabled={disabled} data-testid={testId}>{children}</button>;
}

export function FileUploadBox({ label, file, fileName, onChange, accept = "*", hint = "", onView = null }) {
  const id = `fu-${label.replace(/\s/g, "")}`;
  async function handleChange(e) {
    const f = e.target.files[0];
    if (!f) return;
    const formData = new FormData();
    formData.append("file", f);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(`${API}/files/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });
      onChange({ path: response.data.path, name: f.name, size: f.size, type: f.type });
    } catch (error) {
      console.error("File upload failed:", error);
      alert("File upload failed. Please try again.");
    }
  }
  return (
    <div>
      <label className="text-xs font-medium text-gray-600">{label}</label>
      {file ? (
        <div className="mt-1 flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          <span className="text-green-600 text-lg">{fileName?.match(/\.pdf$/i) ? "PDF" : "IMG"}</span>
          <div className="flex-1 min-w-0">
            {onView ? (
              <button type="button" onClick={onView} className="text-xs font-medium text-green-800 truncate underline hover:text-green-900 text-left w-full" title="Click to view">{fileName}</button>
            ) : (
              <p className="text-xs font-medium text-green-800 truncate">{fileName}</p>
            )}
            <p className="text-xs text-green-600">{onView ? "Uploaded — click the name to view" : "Uploaded"}</p>
          </div>
          <button onClick={() => onChange(null)} className="text-red-400 hover:text-red-600 text-xs ml-2">Remove</button>
        </div>
      ) : (
        <label htmlFor={id} className="mt-1 flex flex-col items-center justify-center border-2 border-dashed border-gray-300 rounded-lg py-4 px-3 cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
          <span className="text-2xl mb-1">+</span>
          <span className="text-xs text-gray-500 text-center">{hint || "Click to upload"}</span>
          <input id={id} type="file" accept={accept} className="hidden" onChange={handleChange} />
        </label>
      )}
    </div>
  );
}

// A time entered as hour + minute + AM/PM, stored as the same 24-hour "HH:MM" string
// every caller already uses.
//
// <input type="time"> renders to the browser's locale. On the shop-floor machines here
// that is a 24-hour box with no AM/PM anywhere, so "quarter past two in the afternoon"
// gets typed as 02:15 and stored as twenty past two in the MORNING. One Unit-III
// breakdown reads 11:10 -> 02:15 on the same day: closed nine hours before it opened,
// which is why it crashed the reliability report instead of counting as downtime.
//
// Three fixed lists remove the ambiguity and the typing: there is no way to key a value
// that is not a real time, and the half of the day is always an explicit choice.
const _HOURS12 = Array.from({ length: 12 }, (_, i) => i + 1);
const _MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));

export function to12Hour(hhmm) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(hhmm || "").trim());
  if (!m) return { hour: "", minute: "", meridiem: "" };
  const h = Number(m[1]);
  if (h < 0 || h > 23) return { hour: "", minute: "", meridiem: "" };
  return {
    hour: String(h % 12 === 0 ? 12 : h % 12),
    minute: m[2],
    // Midnight is 12 AM and noon is 12 PM — the two the 12-hour clock gets wrong most.
    meridiem: h < 12 ? "AM" : "PM",
  };
}

export function to24Hour(hour, minute, meridiem) {
  if (!hour || !minute || !meridiem) return "";
  let h = Number(hour) % 12;
  if (meridiem === "PM") h += 12;
  return `${String(h).padStart(2, "0")}:${minute}`;
}

export function TimeField({ value, onChange, testId, hint }) {
  // The parts are held here, not derived from `value` on every render. They have to be:
  // a time is only a time once all three are chosen, so `value` stays "" while it is
  // being filled in — and reading the boxes back from it meant the first choice was
  // discarded the moment it was made and the field snapped back to "--". Nothing could
  // ever be entered.
  const [draft, setDraft] = useState(() => to12Hour(value));

  // Follow `value` when it changes from outside — a form reset, or an entry loading —
  // but not when it is simply the echo of what is already in the boxes.
  useEffect(() => {
    setDraft(d => (to24Hour(d.hour, d.minute, d.meridiem) === (value || "") ? d : to12Hour(value)));
  }, [value]);

  const set = (patch) => {
    const next = { ...draft, ...patch };
    setDraft(next);
    // "" until the time is complete, so a half-filled box fails the required-field
    // checks rather than silently saving midnight.
    onChange(to24Hour(next.hour, next.minute, next.meridiem));
  };

  // Typed digits are tidied when the box is left, not while it is being typed —
  // rewriting mid-keystroke fights the person doing it.
  const fixHour = () => {
    const n = parseInt(String(draft.hour).replace(/\D/g, ""), 10);
    if (!Number.isFinite(n)) return set({ hour: "" });
    // 24-hour habits are hard to break, so 14 becomes 2 PM and 0 becomes 12 AM rather
    // than being rejected. Anything past 23 is a typo with no sensible reading.
    if (n > 23) return set({ hour: "" });
    if (n === 0) return set({ hour: "12", meridiem: draft.meridiem || "AM" });
    if (n > 12) return set({ hour: String(n - 12), meridiem: "PM" });
    set({ hour: String(n) });
  };
  const fixMinute = () => {
    const n = parseInt(String(draft.minute).replace(/\D/g, ""), 10);
    if (!Number.isFinite(n) || n > 59) return set({ minute: "" });
    set({ minute: String(n).padStart(2, "0") });
  };

  const box = "w-14 border border-gray-300 rounded-lg px-2 py-1.5 text-sm text-center bg-white focus:outline-none focus:ring-2 focus:ring-blue-300";
  const sel = "border border-gray-300 rounded-lg px-2 py-1.5 text-sm bg-white font-semibold focus:outline-none focus:ring-2 focus:ring-blue-300";
  return (
    <div className="mt-0.5">
      <div className="flex items-center gap-1" data-testid={testId}>
        {/* Typeable, with the list as a dropdown for anyone who would rather pick. */}
        <input type="text" inputMode="numeric" maxLength={2} placeholder="hh"
          className={box} value={draft.hour} list={`${testId || "t"}-hours`}
          onChange={e => set({ hour: e.target.value.replace(/\D/g, "").slice(0, 2) })}
          onBlur={fixHour} aria-label="Hour"
          data-testid={testId ? `${testId}-hour` : undefined} />
        <datalist id={`${testId || "t"}-hours`}>
          {Array.from({ length: 12 }, (_, i) => <option key={i} value={i + 1} />)}
        </datalist>
        <span className="font-semibold text-gray-400">:</span>
        <input type="text" inputMode="numeric" maxLength={2} placeholder="mm"
          className={box} value={draft.minute} list={`${testId || "t"}-mins`}
          onChange={e => set({ minute: e.target.value.replace(/\D/g, "").slice(0, 2) })}
          onBlur={fixMinute} aria-label="Minute"
          data-testid={testId ? `${testId}-minute` : undefined} />
        <datalist id={`${testId || "t"}-mins`}>
          {["00", "05", "10", "15", "20", "25", "30", "35", "40", "45", "50", "55"]
            .map(m => <option key={m} value={m} />)}
        </datalist>
        <select className={sel} value={draft.meridiem}
          onChange={e => set({ meridiem: e.target.value })}
          aria-label="AM or PM" data-testid={testId ? `${testId}-meridiem` : undefined}>
          <option value="">--</option>
          <option value="AM">AM</option>
          <option value="PM">PM</option>
        </select>
        {value
          ? <span className="ml-1 text-[10px] text-gray-400" title="Stored as 24-hour">{value}</span>
          : (draft.hour || draft.minute || draft.meridiem)
            ? <span className="ml-1 text-[10px] text-amber-600">pick hh, mm and AM/PM</span>
            : null}
      </div>
      {hint && <p className="mt-0.5 text-[10px] text-gray-400">{hint}</p>}
    </div>
  );
}
